import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSiteContext} from '../contexts/site'
import {scheduleIdleAnalytics} from '../utils/common-utils'
import {getColorBarItemGroupId} from '../utils/product-utils'

const getMonetateEnabled = () => {
    try {
        const {
            app: {commerceAPI}
        } = getConfig()
        const {config} = commerceAPI?.monetate || {}
        return config ? JSON.parse(config).enabled : false
    } catch (e) {
        console.log(e.message)
        return false
    }
}

export default function useMonetate() {
    const {isYR} = useSiteContext()

    return {
        push(...args) {
            window.monetateQ = window.monetateQ || []
            window.monetateQ.push(...args)
        },
        cartRows(productItems, giftCertificateItems, purchaseId) {
            const prod = Array.isArray(productItems) ? productItems : []
            const gifts = Array.isArray(giftCertificateItems) ? giftCertificateItems : []
            // Monetate caps identifiers at 50 characters (SFRA libMonetate.lengthCheck).
            const truncate = (value) => (value && value.length > 50 ? value.substr(0, 49) : value)
            const withPurchaseId = (row) =>
                purchaseId ? {...row, purchaseId: truncate(purchaseId)} : row
            const getColorBarProductId = (item) => {
                try {
                    const {metal} = JSON.parse(item.c_fluidConfigureMetadata)
                    const metalItem = prod.find((productItem) => productItem.productId === metal)
                    return getColorBarItemGroupId(metalItem?.productName)
                } catch (e) {
                    return null
                }
            }

            const productRows = prod.flatMap((item) => {
                const quantity = item.quantity || 1
                const isColorBar = item.productId === 'colorbar'
                const baseRow = withPurchaseId({
                    currency: item.currency || 'USD',
                    productId:
                        truncate(
                            (isColorBar && getColorBarProductId(item)) ||
                                item.c_masterProductId ||
                                item.productId
                        ) || null,
                    quantity,
                    sku:
                        truncate(
                            (isColorBar && item.c_fluidConfigureProductID) ||
                                item.productId ||
                                item.sku
                        ) || null,
                    // SFRA sends getAdjustedPrice(), i.e. after item and prorated order
                    // discounts; SCAPI's `price` is the undiscounted line total.
                    unitPrice: (
                        ((item.c_geOriginalPriceBookTotalPrice ??
                            item.priceAfterOrderDiscount ??
                            item.priceAfterItemDiscount ??
                            item.price) ||
                            0) / quantity
                    ).toFixed(2)
                })

                // SFRA emits a separate row per priced option line item (e.g. engraving).
                const optionRows = (item.optionItems || [])
                    .filter((option) => option.optionValueId !== '0' && option.price > 0)
                    .map((option) => {
                        const optionQuantity = option.quantity || 1
                        return withPurchaseId({
                            currency: item.currency || 'USD',
                            productId: truncate(option.optionId || option.itemId) || null,
                            quantity: optionQuantity,
                            unitPrice: (option.price / optionQuantity).toFixed(2)
                        })
                    })

                return [baseRow, ...optionRows]
            })

            const giftRows = gifts.map((giftCert) =>
                withPurchaseId({
                    currency: 'USD', // hardcode
                    productId: 'gift-card', // hardcode, matches SFRA
                    quantity: 1, // hardcode
                    unitPrice: (Number(giftCert.amount) || 0).toFixed(2)
                })
            )

            return [...productRows, ...giftRows]
        },
        getDateDifference(creationDate) {
            if (!creationDate) {
                return 'n/a'
            }
            const creation = new Date(creationDate)
            const today = new Date()

            const differenceInTime = today - creation
            return Math.floor(differenceInTime / (1000 * 60 * 60 * 24)) // Convert milliseconds to days
        },
        setPLPSwatch(value) {
            window.monetateData = window.monetateData || {}
            window.monetateData.plpSwatch = window.monetateData.plpSwatch || []
            window.monetateData.plpSwatch.push(value)
        },
        setCategoriesViewed(categoryName) {
            window.monetateData = window.monetateData || {}
            const existingCategories = localStorage.getItem('monetate-categoriesViewed')
            localStorage.setItem(
                'monetate-categoriesViewed',
                existingCategories ? `${existingCategories},${categoryName}` : categoryName
            )
        },
        setNavigationClicks(categoryName) {
            window.monetateData = window.monetateData || {}
            window.monetateData.navigationClicks = window.monetateData.navigationClicks || []
            window.monetateData.navigationClicks.push(categoryName)
        },
        setUsedChatBot() {
            scheduleIdleAnalytics(() => {
                window.monetateData = window.monetateData || {}
                window.monetateData.usedChatBot = true
            }, 0)
        },
        setUsedWishlist() {
            scheduleIdleAnalytics(() => {
                window.monetateData = window.monetateData || {}
                window.monetateData.usedWishlist = true
            }, 0)
        },
        setPriceFilter(value) {
            localStorage.setItem('monetate_priceFilter', value.label?.replace('-', 'to') || '')
        },
        addProducts(hits) {
            var productsArray = []
            hits?.forEach((hit) => {
                productsArray.push({
                    productId:
                        hit?.hitType === 'product'
                            ? hit?.representedProduct?.c_masterVariantID
                            : hit.productId || hit?.master?.masterId,
                    sku: hit?.representedProduct?.id || hit?.id
                })
            })
            return productsArray
        },
        setCustomVariables(isRegistered, wishlistLength) {
            return [
                {
                    name: 'Logged In Status',
                    value: isRegistered
                },
                {
                    name: 'Birthday Month',
                    value: isRegistered ? window.birthdayMonth || 'n/a' : 'n/a'
                },
                {
                    name: 'Days Since Last Purchase',
                    value:
                        isRegistered && window.lastOrderDate && window.lastOrderDate !== 'n/a'
                            ? this.getDateDifference(window.lastOrderDate)
                            : 'n/a'
                },
                {
                    name: 'PDP-Color',
                    value: localStorage.getItem('monetateSwatch-color') || ''
                },
                {
                    name: 'PDP-Metal',
                    value: localStorage.getItem('monetateSwatch-metal') || ''
                },
                {
                    name: 'PLP-Swatch',
                    value: window?.monetateData?.plpSwatch || ''
                },
                {
                    name: 'Category Filter',
                    value: localStorage.getItem('monetate-c_classCode') || 'n/a'
                },
                {
                    name: 'Last Metal Filter',
                    value: localStorage.getItem('monetate-c_metal') || ''
                },
                {
                    name: 'Last Sort',
                    value: localStorage.getItem('plpSort') || ''
                },
                {
                    name: 'Categories Viewed',
                    value: localStorage.getItem('monetate-categoriesViewed') || ''
                },
                {
                    name: 'Navigation Clicks',
                    value: window?.monetateData?.navigationClicks || ''
                },
                {
                    name: 'Last Price Range',
                    value: localStorage.getItem('monetate_priceFilter') || ''
                },
                {
                    name: 'Used Save For Later',
                    value: false
                },
                {
                    name: 'Saved For Later Count',
                    value: '' // TODO: When implement Cart Page in PWA, make this dynamic
                },
                {
                    name: 'Used Reviews',
                    value: false // TODO: When implement the PDP in PWA, make this dynamic
                },
                {
                    name: 'Used Wishlist',
                    value: !!window?.monetateData?.usedWishlist
                },
                {
                    name: 'Wishlist Count',
                    value: wishlistLength || 0
                },
                {
                    name: 'Used Site Search',
                    value: !!window.localStorage.getItem('product_search_term')
                },
                {
                    name: 'Philantropy Shopper',
                    value: isRegistered ? !!window.monetatePhilantropyShopper : false
                },
                {
                    name: 'Used ChatBot',
                    value: !!window?.monetateData?.usedChatBot
                },
                {
                    name: 'Brand',
                    value: isYR ? 'Yellow Rose' : 'Kendra Scott'
                }
            ]
        },
        nonPageView() {
            this.push([
                'trackData',
                {
                    nonPageView: true
                }
            ])
        },
        track404Page() {
            if (!getMonetateEnabled()) {
                return
            }
            this.push(['setPageType', '404'])
            this.push(['trackData'])
        },
        buildProductBreadcrumbs(product) {
            const trail = product?.c_breadcrumbs
                ? product.c_breadcrumbs.map((b) => b.htmlValue).join('/') + '/'
                : ''
            return ('Home/' + trail + product?.name).split('/')
        },
        monetateTrackPage(
            idString,
            breadcrumb,
            productItems,
            customer,
            wishlistLength,
            hits = [],
            searchTerm,
            itemsAdded,
            giftCertificateItems,
            options = {}
        ) {
            if (!getMonetateEnabled()) {
                return
            }
            // Delayed base-page trackers (PDP/PLP polling) must not overwrite Quickview while
            // the overlay is open; addToBagModal is still allowed through.
            if (
                window.monetateQuickviewActive &&
                idString !== 'Quickview' &&
                idString !== 'addToBagModal'
            ) {
                return
            }
            // purchaseId switches the rows to addPurchaseRows; the checkout steps skip
            // custom variables and the previous-data stash to match SFRA.
            const {skipCustomVariables, skipPreviousData, purchaseId} = options
            const cartItems = this.cartRows(productItems, giftCertificateItems, purchaseId)
            const customVariables = skipCustomVariables
                ? null
                : this.setCustomVariables(customer, wishlistLength)
            if (hits) {
                const addProductsList = this.addProducts(hits)
                if (idString === 'product' || idString === 'Quickview') {
                    this.push(['addProductDetails', addProductsList])
                } else {
                    this.push(['addProducts', addProductsList])
                }
            }
            if (idString === 'addToBagModal' && itemsAdded?.length) {
                let pDetails = []
                itemsAdded.forEach((addedItem) => {
                    pDetails.push({
                        productId: addedItem?.product?.master?.masterId || addedItem?.masterId,
                        sku: addedItem?.variant?.productId || addedItem?.variantId
                    })
                })
                window.monetateQ.push(['addProductDetails', pDetails])
            }
            if (idString === 'search') {
                this.push([
                    'addSearch',
                    {
                        searchTerm: searchTerm,
                        searchType: 'site'
                    }
                ])
            }
            this.push(['setPageType', idString])
            this.push(['addBreadcrumbs', breadcrumb])
            this.push([purchaseId ? 'addPurchaseRows' : 'addCartRows', cartItems])
            if (!skipCustomVariables) {
                this.push(['setCustomVariables', customVariables])
            }
            this.push(['trackData'])
            // We are saving the following data only to perform retracking after the user closes overlay modals.
            if (idString !== 'addToBagModal' && idString !== 'Quickview' && !skipPreviousData) {
                window.mtPreviousData = {
                    idString: idString,
                    breadcrumb: breadcrumb,
                    customer: customer,
                    wishlistLength: wishlistLength,
                    hits: hits,
                    searchTerm: searchTerm,
                    itemsAdded: itemsAdded
                }
            }
        }
    }
}
